import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Tag, Plus, Pencil, ChevronLeft, CheckCircle2, XCircle, AlertCircle, Save, X,
} from "lucide-react";
import {
  useGetAdminCategoriesQuery,
  useAdminCreateCategoryMutation,
  useAdminUpdateCategoryMutation,
  useAdminToggleCategoryMutation,
  TicketCategory,
} from "../api/supportApi";
import PageGate from "../../../components/PageGate";
import { PERMS } from "../../../utils/perms";

type FormState = { name: string; description: string };
const EMPTY: FormState = { name: "", description: "" };

export default function AdminCategoriesPage() {
  const navigate = useNavigate();
  const { data: categories = [], isLoading } = useGetAdminCategoriesQuery();
  const [createCategory, { isLoading: creating }] = useAdminCreateCategoryMutation();
  const [updateCategory, { isLoading: updating }] = useAdminUpdateCategoryMutation();
  const [toggleCategory]                           = useAdminToggleCategoryMutation();

  const [showForm,    setShowForm]    = useState(false);
  const [editTarget,  setEditTarget]  = useState<TicketCategory | null>(null);
  const [form,        setForm]        = useState<FormState>(EMPTY);
  const [error,       setError]       = useState<string | null>(null);

  function openCreate() {
    setEditTarget(null);
    setForm(EMPTY);
    setError(null);
    setShowForm(true);
  }

  function openEdit(cat: TicketCategory) {
    setEditTarget(cat);
    setForm({ name: cat.name, description: cat.description ?? "" });
    setError(null);
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditTarget(null);
    setForm(EMPTY);
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (editTarget) {
        await updateCategory({
          id:          editTarget.id,
          name:        form.name,
          description: form.description || undefined,
        }).unwrap();
      } else {
        await createCategory({
          name:        form.name,
          description: form.description || undefined,
        }).unwrap();
      }
      closeForm();
    } catch (err: any) {
      setError(
        err?.data?.message ??
        err?.data?.errors?.name?.[0] ??
        "Failed to save category",
      );
    }
  }

  const active   = categories.filter((c) => c.is_active);
  const inactive = categories.filter((c) => !c.is_active);

  return (
    <PageGate perm={PERMS.support.categoryManage}>
      <div className="min-h-screen bg-[#f8f9fa] p-4 md:p-8">
        <div className="max-w-3xl mx-auto space-y-6">

          {/* Header */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-600 text-sm font-bold rounded-xl hover:bg-gray-50 transition-all shadow-sm active:scale-95"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 rounded-2xl bg-gray-900 flex items-center justify-center shadow-lg shadow-gray-900/20">
                <Tag className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-black text-gray-900 tracking-tight">Ticket Categories</h1>
                <p className="text-xs text-gray-500">{active.length} active · {inactive.length} inactive</p>
              </div>
            </div>
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white text-sm font-bold rounded-xl hover:bg-gray-800 transition-all shadow-md shadow-gray-900/20 active:scale-95"
            >
              <Plus className="w-4 h-4" />
              New Category
            </button>
          </div>

          {/* Inline Create / Edit Form */}
          {showForm && (
            <form
              onSubmit={handleSubmit}
              className="bg-white border border-blue-200 rounded-2xl p-6 shadow-sm space-y-4"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-black text-gray-900">
                  {editTarget ? `Edit "${editTarget.name}"` : "New Category"}
                </h2>
                <button type="button" onClick={closeForm} className="text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-xl text-xs text-red-700">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  required
                  maxLength={100}
                  placeholder="e.g. Technical Issue"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 transition"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Description</label>
                <input
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  maxLength={255}
                  placeholder="Short description shown to members"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 transition"
                />
              </div>

              <div className="flex items-center gap-3 pt-1">
                <button
                  type="submit"
                  disabled={creating || updating}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white text-sm font-bold rounded-xl hover:bg-gray-800 transition-all shadow-md shadow-gray-900/20 disabled:opacity-60 active:scale-95"
                >
                  <Save className="w-4 h-4" />
                  {creating || updating ? "Saving…" : editTarget ? "Save Changes" : "Create Category"}
                </button>
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-5 py-2.5 border border-gray-200 text-gray-600 text-sm font-bold rounded-xl hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Category List */}
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-white rounded-2xl animate-pulse border border-gray-100" />
              ))}
            </div>
          ) : categories.length === 0 ? (
            <div className="bg-white border border-gray-100 rounded-3xl p-12 shadow-sm text-center">
              <Tag className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-semibold">No categories yet</p>
              <p className="text-gray-400 text-sm mt-1">Create your first category above</p>
            </div>
          ) : (
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
              {/* Active */}
              {active.length > 0 && (
                <>
                  <div className="px-5 py-2.5 bg-gray-50 border-b border-gray-100">
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Active ({active.length})
                    </span>
                  </div>
                  <ul className="divide-y divide-gray-50">
                    {active.map((cat) => (
                      <CategoryRow
                        key={cat.id}
                        cat={cat}
                        onEdit={() => openEdit(cat)}
                        onToggle={() => toggleCategory(cat.id)}
                      />
                    ))}
                  </ul>
                </>
              )}

              {/* Inactive */}
              {inactive.length > 0 && (
                <>
                  <div className="px-5 py-2.5 bg-gray-50 border-t border-b border-gray-100">
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Inactive ({inactive.length})
                    </span>
                  </div>
                  <ul className="divide-y divide-gray-50">
                    {inactive.map((cat) => (
                      <CategoryRow
                        key={cat.id}
                        cat={cat}
                        onEdit={() => openEdit(cat)}
                        onToggle={() => toggleCategory(cat.id)}
                      />
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </PageGate>
  );
}

function CategoryRow({
  cat,
  onEdit,
  onToggle,
}: {
  cat: TicketCategory;
  onEdit: () => void;
  onToggle: () => void;
}) {
  return (
    <li className={`flex items-center gap-4 px-5 py-4 ${!cat.is_active ? "opacity-60" : ""}`}>
      <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${cat.is_active ? "bg-indigo-50 text-indigo-600" : "bg-gray-100 text-gray-400"}`}>
        <Tag className="w-4 h-4" />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-gray-900">{cat.name}</p>
        {cat.description && (
          <p className="text-xs text-gray-400 truncate">{cat.description}</p>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {cat.is_active ? (
          <span className="flex items-center gap-1 text-[10px] font-black text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg border border-emerald-100">
            <CheckCircle2 className="w-3 h-3" /> Active
          </span>
        ) : (
          <span className="flex items-center gap-1 text-[10px] font-black text-gray-400 bg-gray-50 px-2 py-1 rounded-lg border border-gray-200">
            <XCircle className="w-3 h-3" /> Inactive
          </span>
        )}

        <button
          onClick={onEdit}
          className="p-2 rounded-xl text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
          title="Edit"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>

        <button
          onClick={onToggle}
          className={`px-3 py-1.5 text-xs font-bold rounded-xl border transition-colors ${
            cat.is_active
              ? "border-red-100 text-red-600 bg-red-50 hover:bg-red-100"
              : "border-emerald-100 text-emerald-600 bg-emerald-50 hover:bg-emerald-100"
          }`}
        >
          {cat.is_active ? "Disable" : "Enable"}
        </button>
      </div>
    </li>
  );
}
